import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Printer, X, FileText } from "lucide-react";

/**
 * Renders Claude-Sonnet-4.5 markdown pitch dossier with print + close actions.
 * Mirrors the markdown renderer used on the Site Auditor tab.
 */
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let listBuf = [];
  let inList = false;
  const flush = () => {
    if (inList) {
      out.push(<ul key={`ul-${out.length}`} className="list-disc pl-6 my-3 space-y-1">{listBuf}</ul>);
      listBuf = [];
      inList = false;
    }
  };
  lines.forEach((raw, idx) => {
    const ln = raw.trimEnd();
    if (ln.startsWith("# ")) { flush(); out.push(<h1 key={idx} className="font-display text-3xl md:text-4xl font-black tracking-tighter text-black mt-4 mb-2">{ln.slice(2)}</h1>); }
    else if (ln.startsWith("## ")) { flush(); out.push(<h2 key={idx} className="font-display text-lg md:text-xl font-bold mt-3 mb-1 text-black">{ln.slice(3)}</h2>); }
    else if (ln.startsWith("### ")) { flush(); out.push(<h3 key={idx} className="font-display text-base font-bold mt-5 mb-1 text-black uppercase tracking-wider border-b border-black pb-1">{ln.slice(4)}</h3>); }
    else if (ln.startsWith("- ") || ln.startsWith("* ")) { inList = true; listBuf.push(<li key={idx} className="text-sm leading-relaxed">{ln.slice(2)}</li>); }
    else if (ln.startsWith("> ")) { flush(); out.push(<blockquote key={idx} className="border-l-2 border-zinc-400 pl-3 italic text-zinc-600 my-2 text-sm">{ln.slice(2)}</blockquote>); }
    else if (ln.trim() === "") { flush(); }
    else { flush(); out.push(<p key={idx} className="text-sm leading-relaxed my-2">{ln}</p>); }
  });
  flush();
  return out;
}

export default function DossierViewer({ open, onOpenChange, lawsuit }) {
  const onPrint = () => window.print();
  if (!lawsuit) return null;
  const dossier = lawsuit.pipeline_dossier;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-sm border-2 border-zinc-950 p-0 bg-white print-dossier" data-testid="dossier-dialog">
        <DialogHeader className="bg-zinc-950 text-white px-5 py-3 print:hidden">
          <DialogTitle className="font-display font-black tracking-tight text-lg flex items-center gap-2 text-white">
            <FileText className="w-5 h-5" /> Pitch Dossier — {lawsuit.business_name}
          </DialogTitle>
          <DialogDescription className="text-xs font-mono text-zinc-400">
            case {lawsuit.case_number} · {lawsuit.website_url || "no website"}
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 md:px-10 py-6 max-h-[70vh] md:max-h-[75vh] overflow-y-auto text-black">
          {dossier ? renderMarkdown(dossier) : (
            <div className="text-zinc-500 text-sm">No dossier available.</div>
          )}
        </div>
        <DialogFooter className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex justify-between gap-2 print:hidden">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-sm border-zinc-950 font-bold uppercase tracking-wider"
            data-testid="dossier-close-button"
          >
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
          <Button
            onClick={onPrint}
            disabled={!dossier}
            className="rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider"
            data-testid="dossier-print-button"
          >
            <Printer className="w-4 h-4 mr-2" /> Print / PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
